import { Capacitor } from "@capacitor/core";
import { captureException, captureMessage } from "@sentry/browser";
import { env } from "../env";

type NativeAuthPluginAvailability = {
  canUseBrowser: boolean;
  canUseCache: boolean;
  canUseSecurePreferences: boolean;
  canUseSecureStoragePlugin: boolean;
};

let hasReportedMissingNativeAuthPlugins = false;
let hasReportedNativeAuthDetectionError = false;

function getNativeAppRedirectUri(
  path = "localhost",
  searchParams?: URLSearchParams
) {
  const APP_ID = window.appInfos?.id ?? env.VITE_NATIVE_APP_ID;

  const stringSearchParams = searchParams?.toString();
  const nativeAppRedirectUrl = `${APP_ID}://${path || "localhost"}${
    stringSearchParams ? `?${stringSearchParams}` : ""
  }`;

  console.log("nativeAppRedirectUrl", nativeAppRedirectUrl);
  return nativeAppRedirectUrl;
}

function isPluginAvailable(pluginName: string) {
  try {
    return Capacitor.isPluginAvailable(pluginName);
  } catch {
    return false;
  }
}

function getNativeAuthPluginAvailability(): NativeAuthPluginAvailability {
  const canUseBrowser = isPluginAvailable("Browser");
  const canUseSecurePreferences = isPluginAvailable("SecurePreferences");
  // For this plugin we must append the "Plugin" suffix to the plugin name as the plugin itself is badly named
  const canUseSecureStoragePlugin = isPluginAvailable("SecureStoragePlugin");
  const canUseCache = canUseSecureStoragePlugin || canUseSecurePreferences;

  return {
    canUseBrowser,
    canUseCache,
    canUseSecurePreferences,
    canUseSecureStoragePlugin,
  };
}

function reportMissingNativeAuthPluginsOnce(
  availability: NativeAuthPluginAvailability
) {
  if (hasReportedMissingNativeAuthPlugins) {
    return;
  }

  hasReportedMissingNativeAuthPlugins = true;
  captureMessage(
    "🔐🟡 Native plugins not immediately available, but forcing native config",
    {
      extra: availability,
    }
  );
}

function canUseNative() {
  try {
    const isNativePlatform = Capacitor.isNativePlatform();
    if (isNativePlatform) {
      // On native platforms, we MUST use the native Auth0 configuration
      // to ensure the correct redirect_uri (app scheme) is used.
      // If we fall back to web config, Auth0 will redirect to https://app.clovis.app
      // which loads the web app inside the embedded browser, causing a hang.

      const nativeAuthPluginAvailability = getNativeAuthPluginAvailability();

      if (
        !nativeAuthPluginAvailability.canUseBrowser ||
        !nativeAuthPluginAvailability.canUseCache
      ) {
        reportMissingNativeAuthPluginsOnce(nativeAuthPluginAvailability);
      }

      return true;
    }
  } catch (e) {
    if (hasReportedNativeAuthDetectionError) {
      return false;
    }

    hasReportedNativeAuthDetectionError = true;
    captureException(e, {
      extra: {
        ...getNativeAuthPluginAvailability(),
      },
    });
  }
  return false;
}

export { canUseNative, getNativeAppRedirectUri };
